import React, { useEffect } from 'react'
import { Box, CssBaseline, Link, Stack, ThemeProvider, Typography, useMediaQuery } from '@mui/material'
import './App.css'
import Navigation from './components/Navigation'
import Dashboard from './components/Dashboard'
import { DarkTheme, LightTheme } from '@mercedes-benz/mui5-theme'
import Footer from './components/Footer'
import { Trans, useTranslation } from 'react-i18next'
import { setAlert } from './source/slice/AlertSlice'
import { useDispatch, useSelector } from 'react-redux'
import CustomSnackbar from './components/CustomSnackbar'
import axios, { AxiosRequestConfig } from 'axios'
import { IUser } from './interface/IUser'
import { setUser } from './source/slice/UserSlice'
import { useMsal } from '@azure/msal-react'
import { loginRequest } from './config/authConfig'
import NotAuthenticated from './components/NotAuthenticated'
import { format } from 'date-fns'
import { setTheme } from './source/slice/ThemeSlice'
import { InteractionRequiredAuthError, InteractionStatus, SilentRequest } from '@azure/msal-browser'
import { initializeApplicationInsights } from './config/applicationinsights'
import { setOrders } from './source/slice/OrderSlice'
import { IOrdersRef } from './interface/IOrder'
import Maintenance from './components/Maintenance'
import { getAuthConfig } from './utils/authApi'
import MailIcon from '@mui/icons-material/Mail'
import LaunchIcon from '@mui/icons-material/Launch'

export const isLocal = process.env.REACT_APP_LOCAL_DEVELOPMENT === 'true'

const App = () => {
    const { t } = useTranslation()
    const { instance, accounts, inProgress } = useMsal()
    const isAuthenticated = accounts.length > 0
    const dispatch = useDispatch()
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
    const user: IUser = useSelector((state: any) => state.user?.value)

    useEffect(() => {
        const initializeMsal = async () => {
            if (inProgress === InteractionStatus.None) {
                await instance.initialize()
                await instance.loginRedirect(loginRequest).catch((err: Error) => console.error('login failed', err))
            }
        }

        if (!isAuthenticated) {
            initializeMsal()
        } else {
            if (!isLocal) {
                initializeApplicationInsights()
            }
            getCustomSnackbar()
            //setDefaultSelectedDate()
            getUser()
        }

        if (!localStorage.getItem('theme')) {
            dispatch(setTheme(prefersDarkMode ? 'dark' : 'light'))
        }
    }, [inProgress, instance])

    useEffect(() => {
        if (user.worker) {
            if (user?.worker?.mserp_employmentcategory === 'ANÜ' || user?.worker?.mserp_employmentcategory === 'FAK') {
                getOrders()
            }
        }
    }, [user])

    const scope : string = process.env.REACT_APP_SCOPE || ''
    const accessTokenRequest: SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: false,
    }

    const getCustomSnackbar = () => {
        dispatch(setAlert(null))
    }

    const getOrders = async () => {
        let newOrders: IOrdersRef[]

        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    data: {},
                    params: {
                        /*code: process.env.REACT_APP_BACKEND_URL*/
                        date: format(new Date(), 'yyyy-MM-dd'),
                    },
                }
                axios
                    .get(`${process.env.REACT_APP_BACKEND_URL}/Orders`, config)
                    .then((response) => {
                        newOrders = response.data
                        dispatch(setOrders(newOrders))
                    })
                    .catch((error) => {
                        console.log(error)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
            })
    }

    const getUser = async () => {
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    data: {},
                    params: {
                        /*code: process.env.REACT_APP_BACKEND_URL*/
                    },
                }
                axios
                    .get(`${process.env.REACT_APP_BACKEND_URL}/User`, config)
                    .then((response) => {
                        const newUser: IUser = {
                            userid: accounts[0]?.username,
                            token: '',
                            worker: response.data?.worker,
                            resource: response.data?.resource,
                            isSeniorManager: response.data?.isSeniorManager || false,
                        }
                        dispatch(setUser(newUser))
                    })
                    .catch((error) => {
                        console.error(error)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error.status, error)
            })
    }

    //const theme = React.useMemo(() => (prefersDarkMode ? DarkTheme : LightTheme), [prefersDarkMode])
    const theme = useSelector((state: any) => state.theme?.value)

    const maintenance = process.env.REACT_APP_MAINTENANCE ? process.env.REACT_APP_MAINTENANCE === 'true' : false

    return isAuthenticated ? (
        <ThemeProvider theme={theme === 'light' ? LightTheme : DarkTheme}>
            <CssBaseline />
            {!maintenance ? (
                <>
                    <Navigation />
                    <CustomSnackbar />
                    {user.worker !== null || user.resource !== null ? (
                        <>
                            <Dashboard />
                            <Footer />
                        </>
                    ) : (
                        <Box display="flex" flexDirection="column" width="100%" sx={{ textWrap: 'balance', p: { xs: 4, md: 5, lg: 6 }, alignItems: { xs: 'center', md: 'start' }, textAlign: { xs: 'center', md: 'start' } }} gap={2}>
                            <Typography variant="h3" color="error">
                                {t('main.requestError')}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>
                                    <Trans i18nKey="main.userError" values={{ username: user.userid }} components={{ italic: <i /> }} />
                                </Typography>
                                <LaunchIcon color="inherit" fontSize="small" />
                                <Link
                                    href="https://mbti-serviceportal.app.corpintra.net/wm/app-SelfServicePortalTSS/landing-page/0da8c041-fbc7-ea11-4e91-005056b1ba68?view-options=%7B%22dialogId%22:%222db6f3ef-9b9b-eb11-9181-005056b1b743%22,%22type%22:%22SVMChangeRequestType%22,%22viewType%22:%22new%22%7D&presetParams=%7B%22SPSActivityClassBase%22:%7B%22CUSTSSPType%22:%22110%22,%20%22CUST_FOTicketType%22:%20%2210%22%7D%7D"
                                    target="_blank"
                                    rel="noopener"
                                    underline="hover"
                                >
                                    Service Portal
                                </Link>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>{t('navigation.support.serviceportalInfo')}</Typography>
                                <MailIcon color="inherit" fontSize="small" />
                                <Link href={`mailto:mbti-tickets.pool-id@mercedes-benz.com?subject=Request FO Timetracking - User ${user.userid} not found&cc=mbti-navteam@mercedes-benz.com`} target="_blank" rel="noopener" underline="hover">
                                    Support Mail
                                </Link>
                            </Stack>
                        </Box>
                    )}
                </>
            ) : (
                <Maintenance />
            )}
        </ThemeProvider>
    ) : (
        <NotAuthenticated />
    )
}

export default App
